import React, { useEffect, useState } from 'react';
import { ShieldCheck, HeartPulse, TreePine, Map, Megaphone, Sparkles, Quote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, pickCmsShort, pickCmsText, makeSectionCopyPicker } from '../services/publicContentService';

// Named icon pool for CMS-driven goals.
const RESPONSIBILITY_ICON_MAP = { ShieldCheck, HeartPulse, TreePine, Map, Megaphone, Sparkles };

const NationalResponsibility = ({ theme = 'light' }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const isDark = theme === 'dark';

  // CMS-driven intro paragraph (falls back to the original text).
  const [cmsIntro, setCmsIntro] = useState(null); // { ar, en } | null
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('company_info').then((v) => {
      if (!alive) return;
      const val = v && v.responsibility;
      if (val && (val.ar || val.en)) setCmsIntro(val);
    });
    return () => { alive = false; };
  }, []);

  const defaultGoals = [
    {
      icon: ShieldCheck,
      ar: 'الحفاظ على البيئة وحمايتها وتنميتها ومنع تلوثها.',
      en: 'Preserving, protecting, developing, and preventing the pollution of the environment.',
      gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    },
    {
      icon: HeartPulse,
      ar: 'حماية الصحة العامة من مخاطر الأنشطة والأعمال الضارة بالبيئة.',
      en: 'Protecting public health from the risks of activities and actions harmful to the environment.',
      gradient: 'from-amber-400 via-orange-400 to-amber-500',
    },
    {
      icon: TreePine,
      ar: 'المحافظة على الموارد الطبيعية وتنميتها من خلال استخدامها بشكل رشيد.',
      en: 'Conserving and developing natural resources through rationalized utilization.',
      gradient: 'from-green-400 via-emerald-400 to-teal-400',
    },
    {
      icon: Map,
      ar: 'جعل التخطيط البيئي جزءاً لا يتجزأ من التخطيط الشامل للتنمية في جميع المجالات الصناعية والزراعية والحضارية وغيرها.',
      en: 'Integrating environmental planning into the comprehensive development planning across all industrial, agricultural, urban, and other sectors.',
      gradient: 'from-cyan-400 via-sky-400 to-blue-400',
    },
    {
      icon: Megaphone,
      ar: 'رفع الوعي بالقضايا البيئية وغرس حس المسؤولية الفردية والجماعية، وتشجيع الجهود الوطنية غير الحكومية في هذا المجال.',
      en: 'Raising awareness of environmental issues, instilling a sense of individual and collective responsibility towards preserving and improving the environment, and encouraging non-governmental national efforts in this field.',
      gradient: 'from-amber-400 via-orange-400 to-amber-500',
    },
  ];

  // CMS-driven goals list — expects settings.responsibility_goals.items to be
  // an array of { icon, ar, en, gradient }. Missing fields fall back onto the
  // static defaults so a partial payload never breaks the section.
  const [goals, setGoals] = useState(defaultGoals);
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('responsibility_goals').then((v) => {
      if (!alive) return;
      const list = Array.isArray(v?.items) ? v.items : null;
      if (!list || list.length === 0) return;
      const mapped = list.map((raw, i) => {
        const base = defaultGoals[i] || defaultGoals[defaultGoals.length - 1];
        return {
          icon:     RESPONSIBILITY_ICON_MAP[raw.icon] || base.icon,
          ar:       raw.ar || raw.title_ar || base.ar,
          en:       raw.en || raw.title_en || base.en,
          gradient: raw.gradient || base.gradient,
        };
      });
      if (mapped.length > 0) setGoals(mapped);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase B: responsibility_extra — principle title/body + manifesto quote
  const [extra, setExtra] = useState({});
  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('responsibility_extra').then((v) => { if (alive) setExtra(v || {}); });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);

  const principleTitle = pickCmsShort(extra.principle_title, isAr) || (isAr
    ? 'الاقتصاد، والإدارة الفعّالة، وحماية الموارد — أساس عملنا'
    : 'Economy, effective management, and resource protection — the foundation of our work');
  const principleBody  = pickCmsText(extra.principle_body, isAr, 60) || (isAr
    ? 'يستند عمل شركة التوازن البيئي إلى مبادئ وأسس الاقتصاد والإدارة الفعّالة، وسياسات الحفاظ على الموارد الطبيعية المتجددة وغير المتجددة، وحماية البيئة والوقاية من تلوثها والحدّ من تدهورها — في سعي متواصل نحو ما يُعرف اليوم بالتنمية المستدامة.'
    : 'The operations of Al-Tawazun Al-Bee\'ie Company are anchored on the principles of economics, effective management, and resource conservation policies for both renewable and non-renewable natural resources. We are dedicated to protecting the environment, preventing its pollution, mitigating its degradation, and actively pursuing what is known today as Sustainable Development.');
  const manifestoBody  = pickCmsText(extra.manifesto_body, isAr, 60) || (isAr
    ? 'نؤمن بأنّ استدامة كوكبنا تبدأ من استشعارنا العميق للمسؤولية تجاه موارده، وضمان إرث بيئي آمن للأجيال الحالية والقادمة. ومن هذا المنطلق، نعتزم بناء كيان بخبرات عالمية وتخصصات نوعية، لنكون الرواد في الابتكار البيئي إقليمياً ودولياً — مجسّدين قيمنا الراسخة في الولاء والمسؤولية الاجتماعية، والالتزام بحماية الحياة بكافة أشكالها.'
    : 'We firmly believe that the sustainability of our planet begins with a profound sense of responsibility toward its resources, ensuring a secure environmental legacy for current and future generations. Driven by this conviction, we are committed to building an entity powered by global expertise and specialized, high-caliber disciplines, aiming to pioneer environmental innovation at both regional and international levels. In doing so, we embody our deeply rooted values of loyalty and social responsibility, which define the essence of our corporate identity and our unwavering commitment to protecting all forms of life.');

  return (
    <section
      id="national-responsibility"
      dir={isAr ? 'rtl' : 'ltr'}
      className="relative py-32 overflow-hidden transition-colors duration-700"
    >
      {/* Theme-aware base */}
      {isDark ? (
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-blue-950" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/40 via-cyan-50/40 to-slate-50" />
      )}

      {/* Subtle topographic pattern */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: isDark
            ? 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.4) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Ambient glows */}
      <div
        className="absolute top-1/3 left-1/4 w-[480px] h-[480px] rounded-full blur-[110px] pointer-events-none"
        style={{ background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.16)' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[480px] h-[480px] rounded-full blur-[110px] pointer-events-none"
        style={{ background: isDark ? 'rgba(34,211,238,0.12)' : 'rgba(34,211,238,0.16)' }}
      />

      <div className="relative container mx-auto px-4">
        {/* Editorial Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 ${
              isDark
                ? 'bg-emerald-500/10 border border-emerald-500/30'
                : 'bg-emerald-600/10 border border-emerald-700/30'
            }`}
          >
            <Sparkles className={`w-4 h-4 ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`} />
            <span
              className={`text-xs tracking-[0.3em] uppercase ${
                isDark ? 'text-emerald-200' : 'text-emerald-800'
              }`}
            >
              {sec('responsibility_eyebrow', isAr ? 'التزامنا' : 'Our Commitment')}
            </span>
          </div>

          <h2
            className={`text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {isAr ? (
              <>
                {sec('responsibility_heading_prefix', 'مسؤوليتنا')}{' '}
                <span
                  className={`bg-clip-text text-transparent ${
                    isDark
                      ? 'bg-gradient-to-r from-emerald-300 via-cyan-300 to-blue-300'
                      : 'bg-gradient-to-r from-emerald-700 via-cyan-700 to-blue-700'
                  }`}
                >
                  {sec('responsibility_heading_highlight', 'الوطنية')}
                </span>
              </>
            ) : (
              <>
                {sec('responsibility_heading_prefix', 'Our National')}{' '}
                <span
                  className={`bg-clip-text text-transparent ${
                    isDark
                      ? 'bg-gradient-to-r from-emerald-300 via-cyan-300 to-blue-300'
                      : 'bg-gradient-to-r from-emerald-700 via-cyan-700 to-blue-700'
                  }`}
                >
                  {sec('responsibility_heading_highlight', 'Responsibility')}
                </span>
              </>
            )}
          </h2>

          <p
            className={`text-base md:text-lg leading-relaxed max-w-3xl mx-auto ${
              isDark ? 'text-slate-300' : 'text-slate-600'
            }`}
            data-testid="responsibility-intro"
          >
            {(cmsIntro && (isAr ? cmsIntro.ar : cmsIntro.en)) || (isAr
              ? 'تسعى شركة التوازن البيئي إلى الاستثمار في تنفيذ الأعمال المتعلقة بالبيئة، بما يتوافق مع القانون العام للبيئة ولوائحه التنفيذية، انطلاقاً من خمسة أهداف وطنية جوهرية:'
              : 'Al-Tawazun Al-Bee\'ie Company strives to invest in executing environmental works in full compliance with the General Environmental Law and its Executive Regulations, which aim to achieve the following:')}
          </p>
        </div>

        {/* Goals — numbered cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-4 mb-20 max-w-7xl mx-auto">
          {goals.map((goal, i) => {
            const Icon = goal.icon;
            return (
              <article
                key={i}
                data-testid={`responsibility-goal-${i}`}
                className={`group relative overflow-hidden rounded-2xl p-6 animate-fade-in-up
                            transition-all duration-500 cursor-default backdrop-blur-xl
                            hover:-translate-y-1 ${
                              isDark
                                ? 'bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                                : 'bg-white/85 border border-slate-200 hover:bg-white hover:border-slate-300 shadow-sm hover:shadow-2xl'
                            }`}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                {/* Gradient top bar */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${goal.gradient} opacity-70`} />

                {/* Hover glow */}
                <div
                  className={`absolute -inset-px rounded-2xl bg-gradient-to-br ${goal.gradient}
                              opacity-0 group-hover:opacity-[0.08] blur-2xl transition-opacity duration-500`}
                />

                <div className="relative">
                  <div className={`flex items-start justify-between mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${goal.gradient}
                                  flex items-center justify-center shadow-lg
                                  transition-all duration-500
                                  group-hover:scale-110 group-hover:rotate-6`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span
                      className={`text-xs tracking-[0.25em] font-mono ${
                        isDark ? 'text-emerald-300/70' : 'text-emerald-700/80'
                      }`}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <p
                    className={`text-sm leading-relaxed ${
                      isDark ? 'text-slate-200' : 'text-slate-700'
                    }`}
                    style={{ direction: isAr ? 'rtl' : 'ltr' }}
                  >
                    {isAr ? goal.ar : goal.en}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        {/* Closing principle paragraph + manifesto quote */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 max-w-7xl mx-auto items-stretch">
          {/* Principle */}
          <div
            className={`lg:col-span-7 animate-fade-in-up ${isAr ? 'text-right' : 'text-left'}`}
            style={{ animationDelay: '0.6s' }}
          >
            <div className={`flex items-center gap-3 mb-5 ${isAr ? 'flex-row-reverse' : ''}`}>
              <div className={`h-px w-10 ${isDark ? 'bg-cyan-400/40' : 'bg-cyan-700/40'}`} />
              <span
                data-testid="responsibility-operating-label"
                className={`text-sm font-semibold tracking-wider ${
                  isDark ? 'text-cyan-300' : 'text-cyan-800'
                }`}
              >
                {sec('responsibility_operating', isAr ? 'مبادئنا التشغيلية' : 'Our Operating Principles')}
              </span>
            </div>

            <h3
              className={`text-2xl md:text-3xl font-bold mb-5 leading-snug ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
              data-testid="responsibility-principle-title"
            >
              {principleTitle}
            </h3>

            <p
              className={`text-base md:text-lg leading-loose ${
                isDark ? 'text-slate-300' : 'text-slate-700'
              }`}
              data-testid="responsibility-principle-body"
            >
              {principleBody}
            </p>
          </div>

          {/* Manifesto Quote */}
          <div
            className="lg:col-span-5 animate-fade-in-up"
            style={{ animationDelay: '0.7s' }}
          >
            <div
              className={`relative h-full rounded-3xl p-8 lg:p-10 overflow-hidden ${
                isDark
                  ? 'bg-gradient-to-br from-emerald-900/30 via-cyan-900/30 to-blue-900/30 border border-cyan-500/20'
                  : 'bg-gradient-to-br from-emerald-100 via-cyan-50 to-blue-50 border border-emerald-200'
              }`}
            >
              {/* Decorative quote mark */}
              <Quote
                className={`absolute ${isAr ? 'left-6' : 'right-6'} top-6 w-20 h-20 ${
                  isDark ? 'text-cyan-400/15' : 'text-cyan-600/15'
                }`}
                strokeWidth={1.5}
              />

              <div className="relative">
                <div className={`flex items-center gap-3 mb-5 ${isAr ? 'flex-row-reverse' : ''}`}>
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isDark
                        ? 'bg-gradient-to-br from-emerald-400 to-cyan-400'
                        : 'bg-gradient-to-br from-emerald-500 to-cyan-500'
                    } shadow-md`}
                  >
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <span
                    className={`text-sm font-semibold tracking-wider uppercase ${
                      isDark ? 'text-emerald-300' : 'text-emerald-800'
                    }`}
                  >
                    {sec('responsibility_pledge', isAr ? 'إيماننا' : 'Our Pledge')}
                  </span>
                </div>

                <p
                  className={`text-base md:text-lg leading-loose font-medium ${
                    isDark ? 'text-slate-100' : 'text-slate-800'
                  } ${isAr ? 'text-right' : 'text-left'}`}
                  style={{ direction: isAr ? 'rtl' : 'ltr' }}
                  data-testid="responsibility-manifesto-body"
                >
                  {manifestoBody}
                </p>

                <div
                  className={`mt-6 inline-flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`h-px w-8 ${
                      isDark ? 'bg-emerald-400/50' : 'bg-emerald-700/50'
                    }`}
                  />
                  <span
                    className={`text-xs tracking-[0.3em] uppercase ${
                      isDark ? 'text-emerald-300/80' : 'text-emerald-800/80'
                    }`}
                  >
                    {isAr ? 'شركة التوازن البيئي' : 'Environmental Balance Co.'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NationalResponsibility;
